/**
 * DataFinder（火山引擎）埋点公共实现
 *
 * 各子项目的 lib/bridge.ts 只负责提供「站内如何把事件发给 Native」，
 * 站外的火山 SDK 初始化与上报、公共字段补全都在这里，避免每个项目复制一份。
 *
 * 埋点分流（与旧 ChiefBridge.sendDatafinder 一致）：
 * - 站内（App WebView）：发事件给 Native，由 Native 统一上报
 * - 站外（浏览器）：H5 自行初始化火山 SDK 直接上报
 *
 * 子项目不再 new ChiefBridge（旧库构造时会初始化火山 SDK），
 * 这里的 ensureDataFinder 是唯一初始化入口，不会重复初始化。
 *
 * 用法（bridge.ts 中）：
 *   export const sendDatafinder = createTypedDatafinderSender((eventName, params) =>
 *     bridge.send("sendDatafinder", { eventName, params }),
 *   );
 */
import Sdk from "@datarangers/sdk-javascript";
import { isEmbedded, type DataFinderData } from "./chief-bridge";

/** 站内把埋点转交给 Native 的实现，由各项目的 bridge 提供 */
export type DatafinderNativeSender = (
  eventName: string,
  params: Record<string, string>,
) => void;

/** 不限制事件名和参数结构的埋点函数 */
export type DatafinderSender = (
  eventName: string,
  params: Record<string, string>,
) => void;

/** 按 DataFinderData 约束事件名和参数结构的埋点函数 */
export type TypedDatafinderSender = <E extends keyof DataFinderData>(
  eventName: E,
  params: DataFinderData[E],
) => void;

const isProd = __MODE__ === "prod";
const dataFinderAppId = isProd ? 20004063 : 20004064;

let dataFinderInitialized = false;

/** 惰性初始化 DataFinder，仅站外使用，首次上报前执行 */
function ensureDataFinder(): void {
  if (dataFinderInitialized) return;
  try {
    Sdk.init({
      enable_debug: !isProd,
      log: !isProd,
      app_id: dataFinderAppId,
      channel: "sg",
      channel_domain: "https://gator.uba.ap-southeast-1.volces.com",
    });
    /*
     * 合规要求：埋点只能上报 userid，禁止上传 clientCode（客户账号属于个人信息）。
     * 旧共享库 chief-bridge.ts 会取 clientCode，这里有意不与之对齐。
     */
    const userid = localStorage.getItem("userid") || "";
    Sdk.config({ user_unique_id: userid, user_id: userid });
    Sdk.start();
    dataFinderInitialized = true;
  } catch (error) {
    console.error("DataFinder init error:", error);
  }
}

/**
 * 创建埋点函数。m_id / t_id / s_id / e_id 缺省补 "w"，并附带 uuid（userid）。
 * 事件名和参数不做类型约束，由调用方的包装类型负责。
 */
export function createDatafinderSender(
  sendToNative: DatafinderNativeSender,
): DatafinderSender {
  return (eventName, params) => {
    const data: Record<string, string> = {
      ...params,
      m_id: params.m_id || "w",
      t_id: params.t_id || "w",
      s_id: params.s_id || "w",
      e_id: params.e_id || "w",
      uuid: localStorage.getItem("userid") || "",
    };

    if (isEmbedded()) {
      sendToNative(eventName, data);
      return;
    }

    ensureDataFinder();
    try {
      Sdk.event(eventName, data);
    } catch (error) {
      console.error("DataFinder event error:", error);
    }
  };
}

/** 创建按 DataFinderData 约束事件名和参数的埋点函数 */
export function createTypedDatafinderSender(
  sendToNative: DatafinderNativeSender,
): TypedDatafinderSender {
  // DataFinderData 的各事件参数都是 string 字段的对象，运行时与 Record<string, string> 一致
  return createDatafinderSender(sendToNative) as unknown as TypedDatafinderSender;
}
